// MeditationPlayer.tsx — a standalone guided-meditation video player. Unlike the
// programme's VideoLesson it carries no chapter/completion/transcript chrome: a
// meditation is something to return to any time, not a lesson to tick off.
// Cross-platform: on web the close control sits in a toolbar above the player so
// it never covers the browser's native video controls; on iOS/Android it floats.
import React from 'react';
import { Display, Meta, Pressable } from '@/components/ui';
import { Icon } from '@/components/icons';
import { BackButton } from '@/components/BackButton';
import { TRIAGE_V2 } from '@/config/features';
import { Capacitor } from '@capacitor/core';
import type { Meditation } from '@/data/meditations';

export function MeditationPlayer({ meditation, onClose }: { meditation: Meditation; onClose: () => void }) {
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const [failed, setFailed] = React.useState(false);
  const isWeb = !Capacitor.isNativePlatform();

  const onLoadedMetadata = () => {
    const v = videoRef.current;
    if (!v) return;
    v.play().catch(() => {
      v.muted = true;
      v.play().catch(() => {
        /* blocked — leave paused with controls showing */
      });
    });
  };

  const enterFullscreen = () => {
    const v = videoRef.current as (HTMLVideoElement & { webkitEnterFullscreen?: () => void }) | null;
    if (!v) return;
    if (typeof v.webkitEnterFullscreen === 'function') v.webkitEnterFullscreen();
    else if (typeof v.requestFullscreen === 'function') v.requestFullscreen().catch(() => {});
  };

  const closeBtn = (style: React.CSSProperties) => TRIAGE_V2 ? (
    <BackButton onClick={onClose} style={isWeb ? undefined : { position: 'absolute', top: 'calc(16px + var(--safe-top))', left: 22, zIndex: 3, color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,0.6)' }} />
  ) : (
    <Pressable as="button" onClick={onClose} ariaLabel="Close meditation" style={style}>
      <Icon name="chevDown" size={20} />
    </Pressable>
  );
  const webBtn: React.CSSProperties = {
    width: 42, height: 42, borderRadius: 999, border: '1px solid rgba(246,224,190,0.16)',
    background: 'rgba(14,10,6,0.55)', color: '#F6E6C5', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
  };
  const floatBtn = (right?: number): React.CSSProperties => ({
    position: 'absolute', top: 'calc(16px + var(--safe-top))', left: right == null ? 18 : undefined, right,
    width: 44, height: 44, borderRadius: 999, border: '1px solid rgba(246,224,190,0.16)',
    background: 'rgba(14,10,6,0.45)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
    color: '#F6E6C5', display: 'flex', alignItems: 'center', justifyContent: 'center',
  });

  return (
    <div
      style={{
        position: 'absolute', inset: 0, zIndex: 82, background: 'var(--cream)',
        display: 'flex', flexDirection: 'column', animation: 'rise .42s cubic-bezier(.2,.7,.3,1)',
      }}
    >
      <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', position: 'relative' }}>
        {isWeb && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', padding: '10px 14px', background: '#120c06' }}>
            {closeBtn(webBtn)}
          </div>
        )}
        <div style={{ position: 'relative', background: '#120c06', paddingTop: isWeb ? 0 : 'var(--safe-top)' }}>
          {!failed ? (
            <video
              ref={videoRef}
              src={meditation.video_url}
              poster={meditation.poster_url}
              controls
              autoPlay
              playsInline
              webkit-playsinline="true"
              preload="metadata"
              onLoadedMetadata={onLoadedMetadata}
              onError={() => setFailed(true)}
              style={{ width: '100%', maxHeight: '52vh', display: 'block', background: '#120c06' }}
            />
          ) : (
            <div style={{ aspectRatio: '16 / 10', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, color: '#F6E6C5', textAlign: 'center', padding: 24 }}>
              <Icon name="film" size={30} />
              <span style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, opacity: 0.8 }}>
                This meditation could not load right now.
              </span>
            </div>
          )}
          {!isWeb && closeBtn(floatBtn())}
          {!isWeb && !failed && (
            <Pressable as="button" onClick={enterFullscreen} ariaLabel="Full screen" style={floatBtn(18)}>
              <Icon name="expand" size={18} />
            </Pressable>
          )}
        </div>

        <div style={{ padding: '24px 24px calc(40px + var(--safe-bottom))' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--green-deep)', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            <Icon name="leaf" size={13} /> Guided meditation
          </span>
          <div style={{ marginTop: 10 }}>
            <Display parts={{ pre: meditation.title, em: '' }} size={23} lh={1.16} />
          </div>
          {meditation.minutes != null && (
            <Meta style={{ display: 'block', marginTop: 8 }}>{meditation.minutes} minutes</Meta>
          )}
          <p style={{ marginTop: 18, fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-soft)' }}>
            Find a quiet, comfortable place. There is nothing to achieve here — let the words hold you, and return whenever you need to.
          </p>
        </div>
      </div>
    </div>
  );
}
